// crewbit-admin/src/components/schedule/RouteSummary.tsx
//
// Schedule Module Phase C C4 — admin pair of mobile RouteSummary.tsx.
// Body parallel modulo runtime per feedback_admin_pair_must_match_mobile_body.md.
//
// Spec: docs/references/schedule/SCHEDULE_MODULE_SPEC_v1.md §5.2 + §13.

import { palette } from '../../styles/tokens';
import type { RouteRibbonSummary } from '../../hooks/useRouteRibbonSummary';

export interface RouteSummaryProps {
  summary: RouteRibbonSummary;
  density?: 'compact' | 'normal';
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function relativeOptimized(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return null;
  const minutes = Math.floor((Date.now() - ts) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function RouteSummary({ summary, density = 'normal' }: RouteSummaryProps) {
  const optimized = relativeOptimized(summary.lastOptimizedAt);
  const parts: string[] = [
    `${summary.stopsCount} ${summary.stopsCount === 1 ? 'stop' : 'stops'}`,
    summary.distanceKm > 0 ? `${summary.distanceKm} km` : null,
    summary.driveTimeMinutes > 0 ? `${formatDuration(summary.driveTimeMinutes)} drive` : null,
    optimized ? `optimized ${optimized}` : null,
  ].filter((p): p is string => p != null);

  return (
    <div style={{
      padding: density === 'compact' ? '2px 8px' : '4px 8px',
      fontSize: 12,
      color: palette.g700,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }}>
      {parts.join(' · ')}
    </div>
  );
}
